/*
 * Handles translation config.  Doesn't support a view directly.
 */

define([
  "i18next",
  "moment",
  "configuration/properties",
  "messaging/messenger",
  "locales/translation-en.json",
], function (I18Next, Moment, Properties, Messenger, translationEnJson) {
  "use strict";

  //declare a global moment variable
  window.moment = Moment;

  const DEFAULT_NAMESPACE = "translation";
  const DEFAULT_LANGUAGE = "en";

  /**
   * A custom loader for languages to allow Webpack to properly find and cache bust the JSON
   * files used for translation.  This uses the dynamic import method that Webpack knows to
   * replace with its own routine for loading.
   *
   * @param languages A string with a single language to load, or an array with multiple languages to load
   * @param namespaces A string with a single namespace to load, or an array with multiple namespaces to load
   * @param _options Ignored
   * @param loadComplete A callback method to call when the load is completed
   */
  async function customLoadLanguages(languages, namespaces, _options, loadComplete) {
    try {
      if (Array.isArray(languages) && Array.isArray(namespaces)) {
        const promises = [];
        languages.forEach((language) => {
          namespaces.forEach((namespace) => {
            if (namespace === DEFAULT_NAMESPACE && language === DEFAULT_LANGUAGE) {
              promises.push(Promise.resolve(translationEnJson));
            } else {
              promises.push(import(`locales/${namespace}-${language}.json`));
            }
          });
        });
        const results = await Promise.all(promises);
        loadComplete(null, results);
      } else {
        if (namespaces === DEFAULT_NAMESPACE && languages === DEFAULT_LANGUAGE) {
          loadComplete(null, translationEnJson);
        } else {
          const result = await import(`locales/${namespaces}-${languages}.json`);
          loadComplete(null, result);
        }
      }
    } catch (error) {
      loadComplete(`Failed to load language JSON: ${error}`);
    }
  }

  const option = {
    customLoad: customLoadLanguages,
    useCookies: false,
    lng: Properties.defaultLanguage,
    fallbackLng: Properties.fallbackLanguage,
    getAsync: false,
    useDataAttrOptions: true,
    interpolation: {
      prefix: "__",
      suffix: "__",
    },
  };

  //temporary hack: converts languageId to i18n code
  const langIdToCodeTable = {
    1: "en",
    4: "zh-CN",
    11: "fr",
    12: "de",
    27: "es",
  };

  //for when both language and locale are necessary
  const langLocaleCodeTable = {
    1: "en_US", // English (US)
    2: "ar_SA", // Default Arabic (Egypt) to Arabic (Saudi Arabia)
    3: "bg_BG", // Bulgarian
    4: "zh_CN", // Chinese (Simplified)
    5: "zh_CN", // default Chinese (Traditional) to Chinese (Simplified)
    6: "cs_CZ", // Czech
    7: "da_DK", // Danish
    8: "nl_NL", // Dutch (Netherlands)
    9: "et_EE", // Estonian
    10: "fi_FI", // Finnish
    11: "fr_CA", // French (Canada)
    12: "de_DE", // German (Germany)
    13: "el_GR", // Greek (Greece)
    14: "he_HE", // Hebrew
    15: "hi_IN", // Hindi
    16: "hu_HU", // Hungarian
    17: "it_IT", // Italian (Italy)
    18: "ja_JP", // Japanese
    19: "ko_KR", // Korean
    20: "lt_LT", // Lithuanian
    21: "nb_NO", // Norwegian
    22: "pl_PL", // Polish
    23: "pt_PT", // Portuguese (Portugal)
    24: "ro_RO", // Romanian
    25: "ru_RU", // Russian
    26: "sl_SL", // Slovenian
    27: "es_ES", // Spanish (Spain)
    28: "sv_SE", // Swedish
    29: "en_US", // default Thai to English (US) because Thai is not supported by WebSpellChecker
    30: "tr_TR", // Turkish
    31: "en_AU", // English (Australia)
    32: "en_CA", // English (Canada)
    33: "en_GB", // default English (India) to English (UK)
    34: "en_GB", // default English (Ireland) to English (UK)
    35: "en_GB", // default English (Malta) to English (UK)
    36: "en_NZ", // English (New Zealand)
    37: "en_GB", // default English (Philippines) to English (UK)
    38: "en_GB", // default English (Singapore) to English (UK)
    39: "en_ZA", // English (South Africa)
    40: "en_GB", // English (UK)
    41: "ar_SA", // default Arabic (Algeria) to Arabic (Saudi Arabia)
    42: "ar_SA", // default Arabic (Bahrain) to Arabic (Saudi Arabia)
    43: "ar_SA", // default Arabic (Iraq) to Arabic (Saudi Arabia)
    44: "ar_SA", // default Arabic (Jordan) to Arabic (Saudi Arabia)
    45: "ar_SA", // default Arabic (Kuwait) to Arabic (Saudi Arabia)
    46: "ar_SA", // default Arabic (Lebanon) to Arabic (Saudi Arabia)
    47: "ar_SA", // default Arabic (Libya) to Arabic (Saudi Arabia)
    48: "ar_SA", // default Arabic (Morocco) to Arabic (Saudi Arabia)
    49: "ar_SA", // default Arabic (Oman) to Arabic (Saudi Arabia)
    50: "ar_SA", // default Arabic (Qatar) to Arabic (Saudi Arabia)
    51: "ar_SA", // Arabic (Saudi Arabia)
    52: "ar_SA", // default Arabic (Sudan) to Arabic (Saudi Arabia)
    53: "ar_SA", // default Arabic (Syria) to Arabic (Saudi Arabia)
    54: "ar_SA", // default Arabic (Tunisia) to Arabic (Saudi Arabia)
    55: "ar_SA", // default Arabic (UAE) to Arabic (Saudi Arabia)
    56: "ar_SA", // default Arabic (Yemen) to Arabic (Saudi Arabia)
    57: "zh_CN", // default Chinese (Simplified, Singapore) to Chinese (Simplified)
    58: "zh_CN", // default Chinese (Traditional, Hong Kong) to Chinese (Simplified)
    59: "nl_NL", // default Dutch (Belgium) to Dutch (Netherlands)
    60: "fr_FR", // default French (Belgium) to French (France)
    61: "fr_FR", // French (France)
    62: "fr_FR", // default French (Luxembourg) to French (France)
    63: "fr_FR", // default French (Switzerland) to French (France)
    64: "de_AT", // German (Austria)
    65: "de_DE", // default German (Luxembourg) to German (Germany)
    66: "de_CH", // German (Switzerland)
    67: "el_GR", // default Greek (Cyprus) to Greek (Greece)
    68: "it_IT", // default Italian (Switzerland) to Italian (Italy)
    69: "pt_BR", // Portuguese (Brazil)
    70: "es_AR", // Spanish (Argentina)
    // Changed the language code to Chile for Latin American countries as WellSpellChecker doesn't
    // provide support for them
    71: "es_CL", // Spanish (Bolivia)
    72: "es_CL", // Spanish (Chile)
    73: "es_CO", // Spanish (Columbia)
    74: "es_CR", // Spanish (Costa Rica)
    75: "es_DO", // Spanish (Dominican Republic)
    76: "es_CL", // Spanish (Ecuador)
    77: "es_CL", // Spanish (El Salvador)
    78: "es_CL", // Spanish (Guatemala)
    79: "es_CL", // Spanish (Honduras)
    80: "es_MX", // Spanish (Mexico)
    81: "es_CL", // Spanish (Nicaragua)
    82: "es_CL", // Spanish (Panama)
    83: "es_CL", // Spanish (Paraguay)
    84: "es_PE", // Spanish (Peru)
    85: "es_PR", // Spanish (Puerto Rico)
    86: "es_ES", // default Spanish (US) to Spanish (Spain)
    87: "es_UY", // Spanish (Uruguay)
    88: "es_VE", // Spanish (Venezuela)
    89: "en_US", // default English (Mexico) to English (US)
    90: "en_GB", // default English (Argentina) to English (UK)
    91: "en_GB", // default English (Thailand) to English (UK)
    92: "vi_VN", // Vietnamese (Vietnam)
    93: "en_GB", // default English (UAE) to English (UK)
    94: "en_GB", // default English (Brazil) to English (UK)
    95: "en_GB", // default English (Vietnam) to English (UK)
    96: "en_GB", // default English (Korea) to English (UK)
    97: "en_GB", // default English (Turkey) to English (UK)
  };

  Messenger.command.setHandler(
    "set:interface:language",
    function () {
      //get the interface language for this user
      const langId = Messenger.reqres.request("default:interface:language");
      const code = langIdToCodeTable[langId];
      //moment js uses a different capitalization for chinese, use it here
      let momentCode;
      if (code == "zh-CN") {
        momentCode = "zh-cn";
      } else {
        momentCode = code;
      }
      Moment.locale(momentCode); //set moment datetime i18n language
      I18Next.setLng(code);
    },
    this,
  );

  Messenger.reqres.setHandler(
    "get:interface:language",
    function () {
      const langId = Messenger.reqres.request("default:interface:language");
      return langIdToCodeTable[langId];
    },
    this,
  );

  Messenger.reqres.setHandler(
    "get:interface:targetLanguage",
    function (langId) {
      const languageCode = langLocaleCodeTable[langId];
      if (languageCode) {
        return languageCode.substr(0, languageCode.indexOf("_"));
      }
      //Return english if undefined
      return "en";
    },
    this,
  );

  Messenger.reqres.setHandler(
    "get:interface:langAndLocale",
    function (langId) {
      return langLocaleCodeTable[langId] || "en_US";
    },
    this,
  );

  Messenger.reqres.setHandler("get:moment:date:format", function () {
    return getInterfaceDateFormat();
  });
  Messenger.reqres.setHandler("get:datepicker:date:format", function () {
    let format = getInterfaceDateFormat().toLowerCase();
    if (format.includes("yyyy")) {
      format = format.replace("yyyy", "yy");
    }
    return format;
  });
  Messenger.reqres.setHandler("get:moment:date:format:long", function () {
    return Moment.localeData().longDateFormat("LL");
  });
  Messenger.reqres.setHandler("get:datepicker:date:format:long", function () {
    return Moment.localeData().longDateFormat("LL").toLowerCase();
  });
  Messenger.reqres.setHandler("get:datepicker:dateTime:format", function () {
    return Moment.localeData().longDateFormat("LLL").toLowerCase();
  });
  Messenger.reqres.setHandler("get:moment:dateTime:format", function () {
    return Moment.localeData().longDateFormat("LLL");
  });
  Messenger.reqres.setHandler("get:datepicker:monthly:format", function () {
    return "mm/yy";
  });
  Messenger.reqres.setHandler("get:moment:monthly:format", function () {
    return "MM/YYYY";
  });
  Messenger.reqres.setHandler("get:moment:yearly:format", function () {
    return "YYYY";
  });

  function getInterfaceDateFormat() {
    let dateFormat = Messenger.reqres.request("default:interface:dateFormat");
    if (!dateFormat) {
      dateFormat = Moment.localeData().longDateFormat("L");
    }
    return dateFormat;
  }

  function toMoment(time) {
    if (!time) {
      return null;
    }
    if (typeof time == "string") {
      if (/^\d+$/.test(time)) {
        time = +time;
      } else {
        const lowerTime = time.toLowerCase();
        if (lowerTime == "now" || lowerTime == "today") {
          return Moment();
        }
      }
    }
    return Moment(time);
  }

  function toMomentUtc(time) {
    if (!time) {
      return null;
    }
    if (typeof time == "string") {
      if (/^\d+$/.test(time)) {
        time = +time;
      } else {
        const lowerTime = time.toLowerCase();
        if (lowerTime == "now" || lowerTime == "today") {
          return Moment.utc();
        }
      }
    }
    return Moment.utc(time);
  }

  /**
   * Shorten a MomentJS long date format (e.g. MM/DD/YYYY) to a short date format (e.g. M/D/Y).
   *
   * @param {string}dateFormat The long date format to convert to a short date format.
   *
   * @returns {string} The short date format that corresponds to the long date format given.
   */
  function shortenDateFormat(dateFormat) {
    let separator = "/";
    if (dateFormat.indexOf("/\u200F") > 0) {
      separator = "/\u200F";
    } else if (dateFormat.indexOf("/") > 0) {
      separator = "/";
    } else if (dateFormat.indexOf("-") > 0) {
      separator = "-";
    } else if (dateFormat.indexOf(".") > 0) {
      separator = ".";
    } else {
      return dateFormat;
    }
    const parts = dateFormat.split(separator);
    for (let i = 0; i < parts.length; ++i) {
      parts[i] = parts[i].substring(0, 1);
    }
    dateFormat = parts.join(separator);
    return dateFormat;
  }

  Messenger.reqres.setHandler("get:formatted:date", function (time) {
    const momentObj = toMoment(time);
    if (!momentObj) {
      return "";
    }
    const dateFormat = getInterfaceDateFormat();
    return momentObj.format(dateFormat);
  });

  Messenger.reqres.setHandler("get:formatted:dateUtc", function (time) {
    const momentObj = toMomentUtc(time);
    if (!momentObj) {
      return "";
    }
    const dateFormat = getInterfaceDateFormat();
    return momentObj.format(dateFormat);
  });

  Messenger.reqres.setHandler("get:formatted:date:short", function (time) {
    const momentObj = toMoment(time);
    if (!momentObj) {
      return "";
    }
    const dateFormat = shortenDateFormat(getInterfaceDateFormat());
    return momentObj.format(dateFormat);
  });

  Messenger.reqres.setHandler("get:formatted:dateUtc:short", function (time) {
    const momentObj = toMomentUtc(time);
    if (!momentObj) {
      return "";
    }
    const dateFormat = shortenDateFormat(getInterfaceDateFormat());
    return momentObj.format(dateFormat);
  });

  Messenger.reqres.setHandler("get:formatted:date:long", function (time) {
    const momentObj = toMoment(time);
    if (!momentObj) {
      return "";
    }
    return momentObj.format("LL");
  });

  Messenger.reqres.setHandler("get:formatted:dateUtc:long", function (time) {
    const momentObj = toMomentUtc(time);
    if (!momentObj) {
      return "";
    }
    return momentObj.format("LL");
  });

  Messenger.reqres.setHandler("get:formatted:dateTime", function (time) {
    const momentObj = toMoment(time);
    if (!momentObj) {
      return "";
    }
    return momentObj.format("LLL");
  });

  Messenger.reqres.setHandler("get:seconds:since:epoch", function (time) {
    const momentObj = toMoment(time);
    if (!momentObj) {
      return 0;
    }
    return momentObj.unix();
  });

  Messenger.reqres.setHandler("get:formatted:dateTimeUtc", function (time) {
    const momentObj = toMomentUtc(time);
    if (!momentObj) {
      return "";
    }
    return momentObj.format("LLL");
  });

  Messenger.reqres.setHandler("get:formatted:dateTime:short", function (time) {
    const momentObj = toMoment(time);
    if (!momentObj) {
      return "";
    }
    const dateFormat = shortenDateFormat(getInterfaceDateFormat());
    const timeFormat = "LT";
    const dateString = momentObj.format(dateFormat);
    const timeString = momentObj.format(timeFormat);
    return dateString + " " + timeString;
  });

  Messenger.reqres.setHandler("get:formatted:dateTimeUtc:short", function (time) {
    const momentObj = toMomentUtc(time);
    if (!momentObj) {
      return "";
    }
    const dateFormat = shortenDateFormat(getInterfaceDateFormat());
    const timeFormat = "LT";
    const dateString = momentObj.format(dateFormat);
    const timeString = momentObj.format(timeFormat);
    return dateString + " " + timeString;
  });

  Messenger.reqres.setHandler("get:formatted:dateTime:long", function (time) {
    const momentObj = toMoment(time);
    if (!momentObj) {
      return "";
    }
    return momentObj.format("llll");
  });

  Messenger.reqres.setHandler("get:formatted:dateTimeUtc:long", function (time) {
    const momentObj = toMomentUtc(time);
    if (!momentObj) {
      return "";
    }
    return momentObj.format("llll");
  });

  Messenger.reqres.setHandler("get:formatted:durationSeconds", function (value) {
    if (value === null || value === undefined) {
      return "";
    }
    if (typeof value == "string") {
      if (value.startsWith("PT")) {
        const matches = value
          .substring(2)
          .toLowerCase()
          .match(/[0-9.]+[dhms]/g);
        return matches.join(" ");
      }
      return value;
    }
    if (typeof value == "number") {
      // This is the number of seconds, with an optional number of nanoseconds after a decimal point.
      // Don't really care about the number of nanoseconds
      value = Math.round(value);
      if (value < 60) {
        return value + "s";
      }
      const seconds = value % 60;
      let remaining = Math.floor(value / 60);
      if (remaining < 60) {
        return remaining + "m " + seconds + "s";
      }
      const minutes = remaining % 60;
      remaining = Math.floor(remaining / 60);
      if (remaining < 24) {
        return remaining + "h " + minutes + "m " + seconds + "s";
      }
      const hours = remaining % 24;
      const days = Math.floor(remaining / 24);
      return days + "d " + hours + "h " + minutes + "m " + seconds + "s";
    }
    return value;
  });

  Messenger.reqres.setHandler("get:formatted:durationMilliseconds", function (value) {
    if (value === null || value === undefined) {
      return "";
    }
    if (typeof value == "string") {
      if (value.startsWith("PT")) {
        const matches = value
          .substring(2)
          .toLowerCase()
          .match(/[0-9.]+[dhms]/g);
        return matches.join(" ");
      }
      return value;
    }
    if (typeof value == "number") {
      // This is the number of milliseconds
      value = Math.round(value);
      if (value < 1000) {
        return value + "ms";
      }
      const milliseconds = value % 1000;
      let remaining = Math.floor(value / 1000);
      if (remaining < 60) {
        return remaining + "s " + milliseconds + "ms";
      }
      const seconds = remaining % 60;
      remaining = Math.floor(value / 60);
      if (remaining < 60) {
        return remaining + "m " + seconds + "s " + milliseconds + "ms";
      }
      const minutes = remaining % 60;
      remaining = Math.floor(remaining / 60);
      if (remaining < 24) {
        return remaining + "h " + minutes + "m " + seconds + "s " + milliseconds + "ms";
      }
      const hours = remaining % 24;
      const days = Math.floor(remaining / 24);
      return days + "d " + hours + "h " + minutes + "m " + seconds + "s " + milliseconds + "ms";
    }
    return value;
  });

  Messenger.reqres.setHandler("get:formatted:monthly", function (time) {
    const momentObj = toMoment(time);
    if (!momentObj) {
      return "";
    }
    return momentObj.format("MM/YYYY");
  });

  Messenger.reqres.setHandler("get:formatted:monthlyUtc", function (time) {
    const momentObj = toMomentUtc(time);
    if (!momentObj) {
      return "";
    }
    return momentObj.format("MM/YYYY");
  });

  Messenger.reqres.setHandler("get:formatted:time", function (time) {
    const momentObj = toMoment(time);
    if (!momentObj) {
      return "";
    }
    return momentObj.format("LT");
  });

  Messenger.reqres.setHandler("get:formatted:timeUtc", function (time) {
    const momentObj = toMomentUtc(time);
    if (!momentObj) {
      return "";
    }
    return momentObj.format("LT");
  });

  Messenger.reqres.setHandler("get:iso:date", function (time) {
    const momentObj = toMoment(time);
    if (!momentObj) {
      return "";
    }
    return momentObj.format().substring(0, 10);
  });

  Messenger.reqres.setHandler("get:iso:dateUtc", function (time) {
    const momentObj = toMomentUtc(time);
    if (!momentObj) {
      return "";
    }
    return momentObj.format().substring(0, 10);
  });

  Messenger.reqres.setHandler("get:iso:dateTime", function (time) {
    const momentObj = toMoment(time);
    if (!momentObj) {
      return "";
    }
    return momentObj.format();
  });

  Messenger.reqres.setHandler("get:iso:dateTimeUtc", function (time) {
    const momentObj = toMomentUtc(time);
    if (!momentObj) {
      return "";
    }
    return momentObj.format();
  });

  //
  // These are not really related to internationalization, but since we have code to convert to a Moment here,
  // this is the easiest place to put these
  //
  Messenger.reqres.setHandler("to:moment", (time) => toMoment(time));
  Messenger.reqres.setHandler("to:momentUtc", (time) => toMomentUtc(time));
  Messenger.reqres.setHandler("to:date", (value) => {
    const m = toMoment(value);
    if (m) {
      return m.toDate();
    }
    return null;
  });
  Messenger.reqres.setHandler("to:dateUtc", (value) => {
    const m = toMomentUtc(value);
    if (m) {
      return m.toDate();
    }
    return null;
  });

  I18Next.init(option);

  return I18Next;
});
